import { useEffect, useRef, useCallback, type RefObject } from 'react';

type UseCenterScrollProps<T> = {
  centerId?: number | string;
  items: T[];
};

type UseCenterScrollReturn = {
  scrollRef: RefObject<HTMLDivElement | null>;
  registerItemRef: (id: number | string) => (el: HTMLDivElement | null) => void;
};

export const useCenterScroll = <T>({ centerId, items }: UseCenterScrollProps<T>): UseCenterScrollReturn => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number | string, HTMLDivElement>>(new Map());
  const hasInitialized = useRef(false);

  const scrollToItem = useCallback((itemId: number | string, behavior: ScrollBehavior = 'smooth') => {
    const itemElement = itemRefs.current.get(itemId);
    if (itemElement && scrollRef.current) {
      const scrollContainer = scrollRef.current;
      const itemLeft = itemElement.offsetLeft;
      const itemWidth = itemElement.offsetWidth;
      const containerWidth = scrollContainer.offsetWidth;
      const scrollPosition = itemLeft - (containerWidth - itemWidth) / 2;

      scrollContainer.scrollTo({
        left: scrollPosition,
        behavior,
      });
    }
  }, []);

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    if (!hasInitialized.current) {
      if (centerId && itemRefs.current.has(centerId)) {
        scrollToItem(centerId, 'instant');
        hasInitialized.current = true;
      }
    } else if (centerId) {
      scrollToItem(centerId, 'smooth');
    }
  }, [centerId, items, scrollToItem]);

  const registerItemRef = useCallback(
    (id: number | string) => (el: HTMLDivElement | null) => {
      if (el) {
        itemRefs.current.set(id, el);
      } else {
        itemRefs.current.delete(id);
      }
    },
    [],
  );

  return {
    scrollRef,
    registerItemRef,
  };
};
